import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Tabs,
  Table,
  Form,
  Input,
  DatePicker,
  Button,
  Row,
  Col,
  Space,
  Upload,
  message,
  Dropdown,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  SearchOutlined,
  ImportOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import { TerroristApis } from 'apis/TerroristApis';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

const { TabPane } = Tabs;

const DEFAULT_PAGE_SIZE = 10;

const TerroristList = () => {
  const { setTitle, setBreadcrumb } = useTitle();
  const { user } = useUser();
  const [form] = Form.useForm();

  const [activeTab, setActiveTab] = useState('1');
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setTitle('Danh sách khủng bố & PEPS');
    setBreadcrumb([
      { title: 'Phòng chống khủng bố' },
      { title: 'Danh sách khủng bố' },
    ]);
  }, [setTitle, setBreadcrumb]);

  const fetchData = useCallback(
    async (values?: any, currentPage = 1, currentPageSize = pageSize) => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
          pageSize: currentPageSize,
          ...(values?.customerName &&
            (activeTab === '1'
              ? { fullName: values.customerName }
              : activeTab === '2'
                ? { firstName: values.customerName }
                : { personName: values.customerName })),
          ...(values?.country &&
            (activeTab === '1'
              ? { nationality: values.country }
              : activeTab === '2'
                ? { nationalities: values.country }
                : { country: values.country })),
          ...(values?.updateDateFrom && {
            modifyDateFrom: values.updateDateFrom.toISOString(),
          }),
          ...(values?.updateDateTo && {
            modifyDateTo: values.updateDateTo.toISOString(),
          }),
        };

        const res: any =
          activeTab === '1'
            ? await TerroristApis.getRecords(params)
            : activeTab === '2'
              ? await TerroristApis.getFaft(params)
              : await TerroristApis.getPeps(params);

        const result =
          activeTab === '1'
            ? res?.data?.getCounterTerrorismRecords
            : activeTab === '2'
              ? res?.data?.getCounterTerrorismFaft
              : res?.data?.getCounterTerrorismPeps;

        setDataSource(result?.items ?? []);
        setTotal(result?.pageInfo?.totalItems ?? 0);
      } catch (error) {
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    },
    [activeTab, pageSize],
  );

  useEffect(() => {
    setPage(1);
    fetchData(form.getFieldsValue(), 1, pageSize);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (values: any) => {
    setPage(1);
    fetchData(values, 1, pageSize);
  };

  const handleUpload = async (options: any, type: '1' | '2' | '3') => {
    const { file, onSuccess, onError } = options;
    setLoading(true);
    try {
      if (type === '1') {
        await TerroristApis.importRecords(file as File, user?.id);
      } else if (type === '2') {
        await TerroristApis.importFaft(file as File, user?.id);
      } else {
        await TerroristApis.importPeps(file as File, user?.id);
      }
      message.success('Import dữ liệu thành công!');
      onSuccess?.('ok');
      setPage(1);
      fetchData(form.getFieldsValue(), 1, pageSize);
    } catch (error: any) {
      console.error('Import error:', error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Import dữ liệu thất bại!';
      message.error(errorMsg, 5);
      onError?.(error);
      setLoading(false);
    }
  };

  const downloadTemplate = (type: 'terrorist' | 'faft' | 'peps') => {
    let wsData: any[][] = [];
    let fileName = '';

    let cols: any[] = [];

    if (type === 'terrorist') {
      fileName = 'Mau_Danh_Sach_Khung_Bo.xlsx';
      wsData = [
        [
          'STT',
          'Tên tổ chức',
          'Tên cá nhân',
          'Nick Name / Tên khác',
          'Ngày sinh',
          'Giới tính',
          'Quốc tịch',
          'Quê quán',
          'Địa chỉ',
          'Công việc',
        ],
        [
          1,
          'DNTN KINH DOANH VÀNG THANH TRÚC',
          'Nguyễn Văn A',
          'A Béo',
          '19850101',
          'Nam',
          'Việt Nam',
          'Hà Nội',
          '123 Đường ABC, Hà Nội',
          'Kinh doanh tự do',
        ],
      ];
      cols = [
        { wch: 8 },
        { wch: 35 },
        { wch: 25 },
        { wch: 22 },
        { wch: 15 },
        { wch: 12 },
        { wch: 18 },
        { wch: 20 },
        { wch: 40 },
        { wch: 25 },
      ];
    } else if (type === 'faft') {
      fileName = 'Mau_Danh_Sach_FATF.xlsx';
      wsData = [
        ['DANH SÁCH FATF'],
        [
          'DATAID',
          'VERSIONNUM',
          'FIRST_NAME',
          'SECOND_NAME',
          'THIRD_NAME',
          'UN_LIST_TYPE',
          'REFERENCE_NUMBER',
          'LISTED_ON',
          'NAME_ORIGINAL_SCRIPT',
          'TITLES',
          'DESIGNATIONS',
          'NATIONALITIES',
          'LIST_TYPES',
          'LAST_DAY_UPDATED',
          'TYPE',
          'ALIAS_NAME',
          'ADDRESS',
        ],
        [
          '10001',
          '1',
          'JOHN',
          'DOE',
          '',
          'UN_LIST',
          'REF-001',
          '2022-01-01',
          '',
          'Mr.',
          'Official',
          'United States',
          'Individual',
          '2023-01-01',
          'Individual',
          'JD',
          '123 Main Street',
        ],
      ];
      cols = [
        { wch: 12 },
        { wch: 14 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 18 },
        { wch: 20 },
        { wch: 15 },
        { wch: 25 },
        { wch: 15 },
        { wch: 22 },
        { wch: 20 },
        { wch: 16 },
        { wch: 18 },
        { wch: 16 },
        { wch: 20 },
        { wch: 35 },
      ];
    } else {
      fileName = 'Mau_Danh_Sach_PEPS.xlsx';
      wsData = [
        ['DANH SÁCH CÁ NHÂN CÓ ẢNH HƯỞNG CHÍNH TRỊ (PEPS)'],
        ['Event Date', 'Country', 'Person Name', 'Event Description'],
        ['2022-01-01', 'Vietnam', 'Nguyen Van A', 'Former administrator of...'],
      ];
      cols = [{ wch: 16 }, { wch: 22 }, { wch: 28 }, { wch: 55 }];
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    if (cols.length > 0) {
      ws['!cols'] = cols;
    }
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, fileName);
  };

  const templateMenuItems: MenuProps['items'] = [
    {
      key: 'template-terrorist',
      label: (
        <div
          className="w-full h-full cursor-pointer py-1 px-2"
          onClick={() => downloadTemplate('terrorist')}
        >
          Tải mẫu danh sách tài trợ khủng bố
        </div>
      ),
    },
    {
      key: 'template-faft',
      label: (
        <div
          className="w-full h-full cursor-pointer py-1 px-2"
          onClick={() => downloadTemplate('faft')}
        >
          Tải mẫu danh sách FATF
        </div>
      ),
    },
    {
      key: 'template-peps',
      label: (
        <div
          className="w-full h-full cursor-pointer py-1 px-2"
          onClick={() => downloadTemplate('peps')}
        >
          Tải mẫu danh sách PEPS
        </div>
      ),
    },
  ];

  const importMenuItems: MenuProps['items'] = [
    {
      key: 'import-terrorist',
      label: (
        <Upload
          customRequest={(options) => handleUpload(options, '1')}
          showUploadList={false}
          accept=".xlsx, .xls"
        >
          <div className="w-full h-full cursor-pointer py-1 px-2">
            Import danh sách tài trợ khủng bố
          </div>
        </Upload>
      ),
    },
    {
      key: 'import-faft',
      label: (
        <Upload
          customRequest={(options) => handleUpload(options, '2')}
          showUploadList={false}
          accept=".xlsx, .xls"
        >
          <div className="w-full h-full cursor-pointer py-1 px-2">
            Import danh sách FATF
          </div>
        </Upload>
      ),
    },
    {
      key: 'import-peps',
      label: (
        <Upload
          customRequest={(options) => handleUpload(options, '3')}
          showUploadList={false}
          accept=".xlsx, .xls"
        >
          <div className="w-full h-full cursor-pointer py-1 px-2">
            Import danh sách PEPS
          </div>
        </Upload>
      ),
    },
  ];

  const handleCancel = () => {
    form.resetFields();
  };

  const [scanLoading, setScanLoading] = useState(false);
  const handleTriggerScan = async () => {
    setScanLoading(true);
    try {
      await TerroristApis.triggerScan(user?.id);
      message.success('Đã bắt đầu chạy quét đối chiếu ngầm trên hệ thống.');
    } catch (err) {
      console.error(err);
      message.error('Lỗi khi kích hoạt quét đối chiếu.');
    } finally {
      setScanLoading(false);
    }
  };

  // --- Columns for Tab 1 ---
  const terroristColumns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      render: (text: any, record: any, index: number) =>
        (page - 1) * pageSize + index + 1,
      width: 60,
      align: 'center' as const,
    },
    {
      title: 'Tên tổ chức',
      dataIndex: 'organizationName',
      key: 'organizationName',
    },
    { title: 'Tên cá nhân', dataIndex: 'fullName', key: 'fullName' },
    { title: 'Nick Name / Tên khác', dataIndex: 'nickname', key: 'nickname' },
    { title: 'Ngày sinh', dataIndex: 'bod', key: 'bod' },
    { title: 'Giới tính', dataIndex: 'gender', key: 'gender' },
    { title: 'Quốc tịch', dataIndex: 'nationality', key: 'nationality' },
    { title: 'Quê quán', dataIndex: 'hometown', key: 'hometown' },
    { title: 'Địa chỉ', dataIndex: 'address', key: 'address', ellipsis: true },
    { title: 'Công việc', dataIndex: 'occupation', key: 'occupation' },
    {
      title: 'Người khai báo',
      dataIndex: ['tcreateUser', 'fullName'],
      key: 'tcreateUser',
      render: (text: any, record: any) =>
        record.tcreateUser?.fullName || record.createId || '',
    },
    {
      title: 'Thời gian khai báo',
      dataIndex: 'createDate',
      key: 'createDate',
      render: (value: any) =>
        value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Người cập nhật',
      dataIndex: ['tmodifyUser', 'fullName'],
      key: 'tmodifyUser',
      render: (text: any, record: any) =>
        record.tmodifyUser?.fullName || record.modifyId || '',
    },
    {
      title: 'Thời gian cập nhật',
      dataIndex: 'modifyDate',
      key: 'modifyDate',
      render: (value: any) =>
        value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
  ];

  // --- Columns for Tab 2 ---
  const fatfColumns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      render: (text: any, record: any, index: number) =>
        (page - 1) * pageSize + index + 1,
      width: 60,
      align: 'center' as const,
    },
    { title: 'Mã dữ liệu', dataIndex: 'id', key: 'id' },
    { title: 'Phiên bản dữ liệu', dataIndex: 'versionNum', key: 'versionNum' },
    { title: 'Tên', dataIndex: 'firstName', key: 'firstName' },
    { title: 'Tên đệm', dataIndex: 'secondName', key: 'secondName' },
    { title: 'Tên thứ ba', dataIndex: 'thirdName', key: 'thirdName' },
    {
      title: 'Loại danh sách Liên Hợp Quốc',
      dataIndex: 'unListType',
      key: 'unListType',
    },
    {
      title: 'Số tham chiếu',
      dataIndex: 'referenceNumber',
      key: 'referenceNumber',
    },
    { title: 'Ngày đưa vào danh sách', dataIndex: 'listedOn', key: 'listedOn' },
    {
      title: 'Tên theo ngôn ngữ gốc',
      dataIndex: 'nameOriginalScript',
      key: 'nameOriginalScript',
      ellipsis: true,
    },
    { title: 'Chức danh', dataIndex: 'titles', key: 'titles' },
    {
      title: 'Chức vụ / Phân loại',
      dataIndex: 'designations',
      key: 'designations',
    },
    { title: 'Quốc tịch', dataIndex: 'nationalities', key: 'nationalities' },
    { title: 'Loại danh sách', dataIndex: 'listTypes', key: 'listTypes' },
    {
      title: 'Ngày cập nhật gần nhất',
      dataIndex: 'lastDayUpdated',
      key: 'lastDayUpdated',
    },
    { title: 'Loại đối tượng', dataIndex: 'type', key: 'type' },
    { title: 'Tên bí danh', dataIndex: 'aliasName', key: 'aliasName' },
    { title: 'Địa chỉ', dataIndex: 'address', key: 'address' },
    {
      title: 'Người khai báo',
      dataIndex: ['tcreateUser', 'fullName'],
      key: 'tcreateUser',
      render: (text: any, record: any) =>
        record.tcreateUser?.fullName || record.createId || '',
    },
    {
      title: 'Ngày khai báo',
      dataIndex: 'createDate',
      key: 'createDate',
      render: (value: any) =>
        value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Người cập nhật',
      dataIndex: ['tmodifyUser', 'fullName'],
      key: 'tmodifyUser',
      render: (text: any, record: any) =>
        record.tmodifyUser?.fullName || record.modifyId || '',
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'modifyDate',
      key: 'modifyDate',
      render: (value: any) =>
        value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
  ];

  // --- Columns for Tab 3 (PEPS) ---
  const pepsColumns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      render: (text: any, record: any, index: number) =>
        (page - 1) * pageSize + index + 1,
      width: 60,
      align: 'center' as const,
    },
    { title: 'Ngày sự kiện', dataIndex: 'eventDate', key: 'eventDate' },
    { title: 'Quốc gia', dataIndex: 'country', key: 'country' },
    { title: 'Tên cá nhân', dataIndex: 'personName', key: 'personName' },
    {
      title: 'Mô tả sự kiện',
      dataIndex: 'eventDescription',
      key: 'eventDescription',
      ellipsis: true,
    },
    {
      title: 'Người khai báo',
      dataIndex: ['tcreateUser', 'fullName'],
      key: 'tcreateUser',
      render: (text: any, record: any) =>
        record.tcreateUser?.fullName || record.createId || '',
    },
    {
      title: 'Thời gian khai báo',
      dataIndex: 'createDate',
      key: 'createDate',
      render: (value: any) =>
        value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Người cập nhật',
      dataIndex: ['tmodifyUser', 'fullName'],
      key: 'tmodifyUser',
      render: (text: any, record: any) =>
        record.tmodifyUser?.fullName || record.modifyId || '',
    },
    {
      title: 'Thời gian cập nhật',
      dataIndex: 'modifyDate',
      key: 'modifyDate',
      render: (value: any) =>
        value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
  ];

  return (
    <div className="flex flex-col gap-4 mt-4">
      {/* Top Actions */}
      <Row justify="end">
        <Space>
          <Dropdown menu={{ items: templateMenuItems }} trigger={['click']}>
            <Button icon={<DownloadOutlined />}>Tải file mẫu</Button>
          </Dropdown>
          <Dropdown menu={{ items: importMenuItems }} trigger={['click']}>
            <Button
              icon={<ImportOutlined />}
              type="primary"
              className="bg-[#003B73]"
            >
              Import
            </Button>
          </Dropdown>
        </Space>
      </Row>

      {/* Filter Section */}
      <Card bordered={false} className="shadow-sm rounded-lg">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSearch}
          initialValues={{ updateDateFrom: dayjs('2024-10-20') }}
        >
          <Row gutter={16} align="bottom">
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="customerName"
                label="Tên khách hàng"
                className="mb-0"
              >
                <Input
                  placeholder="Tìm khách hàng"
                  prefix={<SearchOutlined className="text-gray-400" />}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="country" label="Quốc Gia" className="mb-0">
                <Input
                  placeholder="Tìm quốc gia"
                  prefix={<SearchOutlined className="text-gray-400" />}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="updateDateFrom"
                label="Ngày cập nhật từ"
                className="mb-0"
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="dd/mm/yyyy"
                  className="w-full"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="updateDateTo"
                label="Ngày cập nhật đến"
                className="mb-0"
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="dd/mm/yyyy"
                  className="w-full"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end" className="mt-4">
            <Space>
              <Button
                type="dashed"
                onClick={handleTriggerScan}
                loading={scanLoading}
                className="mr-2"
              >
                Quét đối chiếu khách hàng
              </Button>
              <Button onClick={handleCancel}>Huỷ</Button>
              <Button type="primary" htmlType="submit" className="bg-[#003B73]">
                Tìm kiếm
              </Button>
            </Space>
          </Row>
        </Form>
      </Card>

      {/* Table Section */}
      <Card
        bordered={false}
        className="shadow-sm rounded-lg"
        bodyStyle={{ padding: 0 }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="px-4 pt-2"
          tabBarStyle={{ marginBottom: 0 }}
        >
          <TabPane tab="Danh sách khủng bố" key="1" />
          <TabPane tab="Danh sách FATF" key="2" />
          <TabPane tab="Danh sách PEPS" key="3" />
        </Tabs>

        <div className="p-4 overflow-x-auto">
          <Table
            columns={
              (activeTab === '1'
                ? terroristColumns
                : activeTab === '2'
                  ? fatfColumns
                  : pepsColumns) as any
            }
            dataSource={dataSource}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                setPageSize(newPageSize);
                fetchData(form.getFieldsValue(), newPage, newPageSize);
              },
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default TerroristList;
